import {HStack, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {MoneyText} from '@dms/features/new-vehicle';

import type {Nullish} from 'shared/web';

import {NotOfferedFlag, t} from '../utils/catalogueUi';
import {PriceWithGross} from './PriceWithGross';

type TrimPricesProps = {
  rows: {trimId: string; priceNet: string; costNet?: string | Nullish}[];
  trims: {id: string; name: string}[];
  currency: string;
  vatRate: string | Nullish;
  canViewCost: boolean;
};

/** A colour's or package's price on each trim it is offered on; 0 reads Included. */
export function TrimPrices(props: TrimPricesProps) {
  const offered = props.trims.flatMap((trim) => {
    const row = props.rows.find(({trimId}) => trimId === trim.id);
    return isNotNil(row) ? [{trim, row}] : [];
  });
  if (!offered.length) {
    return <NotOfferedFlag />;
  }
  return (
    <VStack spacing={2}>
      {offered.map(({trim, row}) => (
        <HStack key={trim.id} justify="space-between" align="flex-start" spacing={3}>
          <Text size="small">{trim.name}</Text>
          <VStack spacing={0} align="flex-end">
            {match(row.priceNet)
              .with('', () => (
                <Text size="small" color="danger">
                  {t('entity.newVehicle.authoring.noPriceYet', 'No price yet')}
                </Text>
              ))
              .when(
                (priceNet) => Number(priceNet) === 0,
                () => (
                  <Text size="small" color="secondary">
                    {t('entity.newVehicle.authoring.included', 'Included')}
                  </Text>
                )
              )
              .otherwise((priceNet) => (
                <PriceWithGross
                  amount={priceNet}
                  currency={props.currency}
                  vatRate={props.vatRate}
                />
              ))}
            <Show when={props.canViewCost && isNotNil(row.costNet) ? row.costNet : null}>
              {(costNet) => (
                <HStack spacing={1} align="center">
                  <Text size="xSmall" color="secondary">
                    {t('entity.newVehicle.catalogue.costNet', 'Cost net')}
                  </Text>
                  <MoneyText
                    amount={costNet}
                    currency={props.currency}
                    size="xSmall"
                    color="secondary"
                  />
                </HStack>
              )}
            </Show>
          </VStack>
        </HStack>
      ))}
    </VStack>
  );
}
